import type { PersonaId } from '@/lib/codebaby/types';

/** Centralized CodeBaby loader IDs — generated by sales-academy-factory.
 *  Never hardcode loader IDs in screens. Each ID resolves at runtime from a
 *  VITE_CB_LOADER_ID_* env var; in mock mode the value is unused and the
 *  FALLBACK strings keep a non-empty value during development. */

interface PersonaAvatarConfig {
  primaryLoaderId: string;
  loaderUrl: string;
  metaLoaderId: string | null;
}

const FALLBACK = {
  dan: 'sales-academy-sales-academy-real-estate-dan',
  danMeta: 'sales-academy-sales-academy-real-estate-dan-meta',
  rita: 'sales-academy-sales-academy-real-estate-rita',
  ritaMeta: 'sales-academy-sales-academy-real-estate-rita-meta',
  maya: 'sales-academy-sales-academy-real-estate-maya',
  mayaMeta: 'sales-academy-sales-academy-real-estate-maya-meta',
  tyler: 'sales-academy-sales-academy-real-estate-tyler',
  tylerMeta: 'sales-academy-sales-academy-real-estate-tyler-meta',
  coach: 'sales-academy-sales-academy-real-estate-coach',
} as const;

function loaderUrlFor(loaderId: string, envOverride: string | undefined): string {
  if (envOverride && envOverride.length > 0) return envOverride;
  const base = import.meta.env.VITE_BRIDGE3_LOADER_URL;
  if (!base || base.length === 0) return '';
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}id=${encodeURIComponent(loaderId)}`;
}

export const PERSONA_AVATARS: Record<PersonaId, PersonaAvatarConfig> = {
  'dan-kowalski': {
    primaryLoaderId: import.meta.env.VITE_CB_LOADER_ID_DAN ?? FALLBACK.dan,
    loaderUrl: loaderUrlFor(
      import.meta.env.VITE_CB_LOADER_ID_DAN ?? FALLBACK.dan,
      import.meta.env.VITE_CB_LOADER_URL_DAN,
    ),
    metaLoaderId: import.meta.env.VITE_CB_LOADER_ID_DAN_META ?? FALLBACK.danMeta,
  },
  'rita-solano': {
    primaryLoaderId: import.meta.env.VITE_CB_LOADER_ID_RITA ?? FALLBACK.rita,
    loaderUrl: loaderUrlFor(
      import.meta.env.VITE_CB_LOADER_ID_RITA ?? FALLBACK.rita,
      import.meta.env.VITE_CB_LOADER_URL_RITA,
    ),
    metaLoaderId: import.meta.env.VITE_CB_LOADER_ID_RITA_META ?? FALLBACK.ritaMeta,
  },
  'maya-osei': {
    primaryLoaderId: import.meta.env.VITE_CB_LOADER_ID_MAYA ?? FALLBACK.maya,
    loaderUrl: loaderUrlFor(
      import.meta.env.VITE_CB_LOADER_ID_MAYA ?? FALLBACK.maya,
      import.meta.env.VITE_CB_LOADER_URL_MAYA,
    ),
    metaLoaderId: import.meta.env.VITE_CB_LOADER_ID_MAYA_META ?? FALLBACK.mayaMeta,
  },
  'tyler-brandt': {
    primaryLoaderId: import.meta.env.VITE_CB_LOADER_ID_TYLER ?? FALLBACK.tyler,
    loaderUrl: loaderUrlFor(
      import.meta.env.VITE_CB_LOADER_ID_TYLER ?? FALLBACK.tyler,
      import.meta.env.VITE_CB_LOADER_URL_TYLER,
    ),
    metaLoaderId: import.meta.env.VITE_CB_LOADER_ID_TYLER_META ?? FALLBACK.tylerMeta,
  },
  coach: {
    primaryLoaderId: import.meta.env.VITE_CB_LOADER_ID_COACH ?? FALLBACK.coach,
    loaderUrl: loaderUrlFor(
      import.meta.env.VITE_CB_LOADER_ID_COACH ?? FALLBACK.coach,
      import.meta.env.VITE_CB_LOADER_URL_COACH,
    ),
    metaLoaderId: null,
  },
};

export function getPrimaryLoaderId(personaId: PersonaId): string {
  return PERSONA_AVATARS[personaId].primaryLoaderId;
}

export function getLoaderUrl(personaId: PersonaId): string {
  return PERSONA_AVATARS[personaId].loaderUrl;
}

export function getMetaLoaderId(personaId: PersonaId): string | null {
  return PERSONA_AVATARS[personaId].metaLoaderId;
}

export const PERSONA_KEYS = ['dan', 'rita', 'maya', 'tyler'] as const;
export type PersonaKey = (typeof PERSONA_KEYS)[number];

export const AVATAR_IDS = {
  coach: PERSONA_AVATARS['coach'].primaryLoaderId,
  dan: PERSONA_AVATARS['dan-kowalski'].primaryLoaderId,
  rita: PERSONA_AVATARS['rita-solano'].primaryLoaderId,
  maya: PERSONA_AVATARS['maya-osei'].primaryLoaderId,
  tyler: PERSONA_AVATARS['tyler-brandt'].primaryLoaderId,
} as const;
export type AvatarKey = keyof typeof AVATAR_IDS;
export type AvatarLoaderId = (typeof AVATAR_IDS)[AvatarKey];

/** Meta-avatar pattern is replaced by our backend classifier (ADR 0002). */
export const META_AVATAR_IDS: Partial<Record<AvatarLoaderId, string>> = {};

const PERSONA_KEY_TO_ID: Record<PersonaKey, PersonaId> = {
  dan: 'dan-kowalski',
  rita: 'rita-solano',
  maya: 'maya-osei',
  tyler: 'tyler-brandt',
};

const PERSONA_ID_TO_KEY: Partial<Record<PersonaId, PersonaKey>> = {
  'dan-kowalski': 'dan',
  'rita-solano': 'rita',
  'maya-osei': 'maya',
  'tyler-brandt': 'tyler',
};

export function personaKeyToId(key: PersonaKey): PersonaId {
  return PERSONA_KEY_TO_ID[key];
}

export function personaIdToKey(id: PersonaId): PersonaKey | null {
  return PERSONA_ID_TO_KEY[id] ?? null;
}
